'use client';

import { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { ChevronDown, Search, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { cn } from '@/app/utils/cn';
import { NavDemoButtons } from './components/ButtonRequestDemo';
import { hardcodedDropdownData } from './utils/dropdownData';

const SearchModal = dynamic(
    () =>
        import('@/components/shared/chat/SearchModal').then((mod) => ({
            default: mod.SearchModal
        })),
    { ssr: false }
);

// ---------------------------------------------------------------------------
// NavDropdownContent – renders the inner dropdown panel (no positioning)
// ---------------------------------------------------------------------------

const NavDropdownContent = memo(function NavDropdownContent({
    columns,
    promo,
    footer,
    isAnimating
}) {
    const hasPromo = !!promo;
    const colCount = columns.length;

    return (
        <div className="animate-nav-content-in">
            <div
                className={cn(
                    'flex',
                    hasPromo && 'divide-x divide-gray-300/60 dark:divide-blue-600'
                )}>
                <div
                    className={cn(
                        'min-w-0 flex-1 p-7',
                        colCount >= 3
                            ? 'grid grid-cols-3 items-start gap-6'
                            : colCount === 2
                              ? 'grid grid-cols-2 items-start gap-6'
                              : ''
                    )}>
                    {columns.map((column, colIndex) => (
                        <div key={colIndex} className="min-w-0 overflow-hidden">
                            {column.heading && (
                                <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                                    {column.heading}
                                </p>
                            )}
                            <div className="space-y-0">
                                {column.items.map((item) => (
                                    <a
                                        key={item.label}
                                        href={item.href}
                                        className="group flex items-start gap-3 rounded-lg px-4 py-3 transition-all duration-200 hover:bg-fuschia-100 dark:hover:bg-fuschia-600/20">
                                        {item.icon && (
                                            <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 transition-colors group-hover:bg-fuschia-200 dark:bg-custom-on-background-dark dark:group-hover:bg-fuschia-600">
                                                <span className="text-gray-500 transition-colors group-hover:text-fuschia dark:text-gray-300 dark:group-hover:text-fuschia-300 [&>svg]:size-5">
                                                    <NavItemIcon icon={item.icon} />
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex min-w-0 flex-1 flex-col">
                                            <span className="text-sm font-medium leading-normal text-gray-900 transition-colors group-hover:text-fuschia dark:text-white dark:group-hover:text-fuschia-600">
                                                {item.label}
                                            </span>

                                            {item.description && (
                                                <div
                                                    className={cn(
                                                        'grid',
                                                        isAnimating
                                                            ? 'grid-rows-[0fr]'
                                                            : 'grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-out'
                                                    )}>
                                                    <div className="min-h-0 overflow-hidden">
                                                        <p
                                                            className={cn(
                                                                'text-xs leading-snug text-gray-500 transition-[opacity,transform] dark:text-gray-400',
                                                                isAnimating
                                                                    ? 'delay-0 duration-0 translate-y-2 opacity-0'
                                                                    : 'delay-100 duration-300 ease-out translate-y-0 opacity-100'
                                                            )}>
                                                            {item.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {promo && (
                    <div className="flex w-[290px] shrink-0 flex-col p-5">
                        <div
                            className={cn(
                                'flex flex-1 flex-col overflow-hidden rounded-xl',
                                promo.bgClass || 'bg-fuschia-100'
                            )}>
                            {promo.image && (
                                <div className="relative h-[140px] overflow-hidden">
                                    <Image
                                        src={promo.image}
                                        alt={promo.title}
                                        fill
                                        priority
                                        className="object-cover"
                                    />
                                </div>
                            )}
                            <div className="flex flex-1 flex-col justify-between p-5">
                                <div>
                                    <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-fuschia dark:text-fuschia-500">
                                        {promo.eyebrow}
                                    </p>
                                    <p className="mb-2 text-sm font-semibold leading-snug text-gray-900 dark:text-white">
                                        {promo.title}
                                    </p>
                                    <div
                                        className={cn(
                                            'grid',
                                            isAnimating
                                                ? 'grid-rows-[0fr]'
                                                : 'grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-out'
                                        )}>
                                        <div className="min-h-0 overflow-hidden">
                                            <p
                                                className={cn(
                                                    'text-xs leading-relaxed text-gray-500 transition-[opacity,transform] dark:text-gray-300',
                                                    isAnimating
                                                        ? 'delay-0 duration-0 translate-y-2 opacity-0'
                                                        : 'delay-100 duration-300 ease-out translate-y-0 opacity-100'
                                                )}>
                                                {promo.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <a
                                    href={promo.cta?.href || '#'}
                                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold leading-tight text-fuschia hover:underline dark:text-fuschia-500">
                                    {promo.cta?.linkName || 'Read more'}{' '}
                                    <div className="w-6 min-w-6">
                                        <ArrowRight className="size-5" />
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {footer && (
                <div className="border-t border-gray-300/60 bg-gray-50 px-7 py-4 dark:border-blue-600 dark:bg-custom-on-background-dark">
                    {footer}
                </div>
            )}
        </div>
    );
});

// ---------------------------------------------------------------------------
// Icon helper — renders Lucide React elements or dotCMS asset inodes
// ---------------------------------------------------------------------------

export function NavItemIcon({ icon }) {
    if (!icon) return null;

    if (typeof icon === 'string') {
        return (
            <Image
                src={`/dA/${icon}`}
                alt=""
                width={20}
                height={20}
                className="size-5"
                unoptimized
            />
        );
    }

    return icon;
}

export const hardcodedNavItems = [
    { key: 'product', label: 'Product', hasDropdown: true },
    { key: 'solutions', label: 'Solutions', hasDropdown: true },
    { key: 'developers', label: 'Developers', hasDropdown: true },
    { key: 'resources', label: 'Resources', hasDropdown: true },
    { key: 'ecosystem', label: 'Ecosystem', hasDropdown: false, href: '#' },
    { key: 'pricing', label: 'Pricing', hasDropdown: false, href: '#' }
];

// ---------------------------------------------------------------------------
// Width helper
// ---------------------------------------------------------------------------

function getDropdownWidth(key, dd) {
    if (!key || !dd[key]) return 0;
    const { columns, promo } = dd[key];
    const colCount = columns.length;
    const hasPromo = !!promo;
    let width;
    if (colCount >= 3 && hasPromo) width = 1250;
    else if (colCount >= 3 || hasPromo) width = 960;
    else if (colCount === 2) width = 640;
    else width = 380;
    if (key === 'resources') width += 40;
    return width;
}

const PRODUCT_FOOTER = (
    <a
        href="https://www.dotcms.com/features"
        className="flex items-center gap-1.5 text-sm font-medium text-fuschia hover:underline dark:text-fuschia-500">
        See all features
        <ArrowRight className="size-4" />
    </a>
);

function getFooter(key) {
    if (key === 'product') return PRODUCT_FOOTER;
    return undefined;
}

// ---------------------------------------------------------------------------
// Navigation (main export)
// ---------------------------------------------------------------------------

export function mergeHardcodedFallbacks(cmsDropdowns) {
    if (!cmsDropdowns) return null;
    const merged = {};
    for (const key of Object.keys(cmsDropdowns)) {
        const cms = cmsDropdowns[key];
        const hc = hardcodedDropdownData[key];

        // Merge CMS promo content fields with hardcoded visual fallbacks (image, bgClass, description)
        const hcPromo = hc?.promo || null;
        const cmsPromo = cms.promo || null;
        const mergedPromo = cmsPromo
            ? {
                  bgClass: hcPromo?.bgClass || 'bg-fuschia-100',
                  image: hcPromo?.image || null,
                  description: hcPromo?.description || null,
                  ...cmsPromo,
                  cta: {
                      href: cmsPromo.cta?.href || hcPromo?.cta?.href || '#',
                      linkName: cmsPromo.cta?.linkName || hcPromo?.cta?.linkName || 'Read more'
                  }
              }
            : hcPromo;

        const hcItemsByHref = new Map();
        hc?.columns?.forEach((col) => {
            col.items?.forEach((it) => {
                if (it?.href && it.href !== '#') hcItemsByHref.set(it.href, it);
            });
        });

        merged[key] = {
            ...cms,
            promo: mergedPromo,
            columns: cms.columns.map((col, colIdx) => ({
                ...col,
                items: col.items.map((item, itemIdx) => {
                    const hcItem =
                        hc?.columns?.[colIdx]?.items?.[itemIdx] ||
                        hcItemsByHref.get(item.href);
                    return {
                        ...item,
                        icon: hcItem?.icon ?? item.icon ?? null,
                        description: item.description ?? hcItem?.description ?? null
                    };
                })
            }))
        };
    }
    return merged;
}

function Navigation({ dropdowns, navItems, useHardcoded = true, className }) {
    const dropdownData = useMemo(
        () =>
            useHardcoded
                ? hardcodedDropdownData
                : mergeHardcodedFallbacks(dropdowns) || hardcodedDropdownData,
        [useHardcoded, dropdowns]
    );

    const activeNavItems = useMemo(
        () => (useHardcoded ? hardcodedNavItems : navItems || hardcodedNavItems),
        [useHardcoded, navItems]
    );

    const [activeDropdown, setActiveDropdown] = useState(null);
    const [showSearch, setShowSearch] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });
    const timeoutRef = useRef(null);
    const animatingTimerRef = useRef(null);
    const activeDropdownRef = useRef(null);
    const navRef = useRef(null);
    const buttonRefs = useRef({});
    const lastActiveRef = useRef(null);

    useEffect(() => {
        if (activeDropdown) lastActiveRef.current = activeDropdown;
    }, [activeDropdown]);

    const getDropdownLeft = useCallback(
        (key) => {
            if (!key || !navRef.current || !buttonRefs.current[key]) return 0;
            const navRect = navRef.current.getBoundingClientRect();
            const btnRect = buttonRefs.current[key].getBoundingClientRect();
            const btnCenter = btnRect.left + btnRect.width / 2 - navRect.left;
            const width = Math.min(getDropdownWidth(key, dropdownData), window.innerWidth - 32);
            const idealLeft = btnCenter - width / 2;
            const edgePadding = 16;
            const minLeft = edgePadding - navRect.left;
            const maxLeft = window.innerWidth - edgePadding - navRect.left - width;
            return Math.min(Math.max(idealLeft, minLeft), maxLeft);
        },
        [dropdownData]
    );

    const handleMouseEnter = useCallback((key) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setHasInteracted(true);
        if (activeDropdownRef.current !== null && activeDropdownRef.current !== key) {
            setIsAnimating(true);
            if (animatingTimerRef.current) clearTimeout(animatingTimerRef.current);
            animatingTimerRef.current = setTimeout(() => setIsAnimating(false), 250);
        }
        activeDropdownRef.current = key;
        setActiveDropdown(key);
        if (navRef.current && buttonRefs.current[key]) {
            const btn = buttonRefs.current[key];
            setIndicatorStyle({
                left: btn.offsetLeft,
                width: btn.offsetWidth,
                opacity: 1
            });
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        timeoutRef.current = setTimeout(() => {
            if (animatingTimerRef.current) clearTimeout(animatingTimerRef.current);
            setActiveDropdown(null);
            setHasInteracted(false);
            setIsAnimating(false);
            activeDropdownRef.current = null;
            setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
        }, 150);
    }, []);

    const handleDropdownMouseEnter = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }, []);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (animatingTimerRef.current) clearTimeout(animatingTimerRef.current);
        };
    }, []);

    const toggleSearch = useCallback(() => {
        setShowSearch((prev) => !prev);
    }, []);

    const closeSearch = useCallback(() => setShowSearch(false), []);

    const isOpen = !!activeDropdown;
    const displayKey = activeDropdown || lastActiveRef.current;
    const displayData = displayKey ? dropdownData[displayKey] : null;
    const dropdownWidth = getDropdownWidth(displayKey, dropdownData);
    const dropdownLeft = getDropdownLeft(activeDropdown || lastActiveRef.current);

    return (
        <div
            className={cn(
                className,
                'items-center gap-1 lg:gap-4 xl:gap-5 transition-all duration-700 ease-in-out'
            )}>
            <nav ref={navRef} className="relative flex items-center gap-0 lg:gap-1">
                {activeNavItems.map((item) =>
                    item.hasDropdown ? (
                        <button
                            key={item.key}
                            ref={(el) => {
                                buttonRefs.current[item.key] = el;
                            }}
                            onMouseEnter={() => handleMouseEnter(item.key)}
                            onMouseLeave={handleMouseLeave}
                            className={cn(
                                'relative flex items-center gap-0.5 lg:gap-1.5 rounded-lg px-1.5 lg:px-3 xl:px-4 py-2 text-sm lg:text-sm xl:text-base font-medium transition-all duration-200',
                                activeDropdown === item.key
                                    ? 'text-gray-900 dark:text-white'
                                    : 'text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
                            )}>
                            {item.label}
                            <ChevronDown
                                className={cn(
                                    'size-3 lg:size-4 xl:size-5 opacity-40 transition-transform duration-200',
                                    activeDropdown === item.key && 'rotate-180 opacity-70'
                                )}
                            />
                        </button>
                    ) : (
                        <a
                            key={item.key}
                            href={item.href}
                            className="rounded-lg px-1.5 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 lg:px-3 lg:py-2.5 lg:text-sm xl:px-4 xl:text-base dark:text-gray-300 dark:hover:text-white">
                            {item.label}
                        </a>
                    )
                )}

                {/* Sliding active indicator */}
                <span
                    className="pointer-events-none absolute bottom-0 h-[2px] rounded-full bg-fuschia"
                    style={{
                        left: `${indicatorStyle.left}px`,
                        width: `${indicatorStyle.width}px`,
                        opacity: indicatorStyle.opacity,
                        transition:
                            'left 300ms cubic-bezier(0.16,1,0.3,1), width 300ms cubic-bezier(0.16,1,0.3,1), opacity 150ms ease'
                    }}
                />

                {/* Single animated dropdown container */}
                <div
                    className={cn(
                        'absolute top-full z-50 pt-3',
                        isOpen
                            ? 'pointer-events-auto opacity-100'
                            : 'pointer-events-none opacity-0',
                        !hasInteracted && isOpen && 'animate-nav-dropdown'
                    )}
                    style={{
                        left: `${dropdownLeft}px`,
                        width: `${dropdownWidth}px`,
                        maxWidth: 'calc(100vw - 32px)',
                        transition: hasInteracted
                            ? 'left 300ms cubic-bezier(0.16,1,0.3,1), width 300ms cubic-bezier(0.16,1,0.3,1), opacity 200ms ease'
                            : 'opacity 200ms ease'
                    }}
                    onMouseEnter={handleDropdownMouseEnter}
                    onMouseLeave={handleMouseLeave}>
                    <div className="overflow-hidden rounded-2xl border border-gray-300/60 bg-white shadow-[0_20px_40px_-12px_rgba(20,20,43,0.12)] dark:border-blue-600 dark:bg-[#0c1231]">
                        {displayData && (
                            <NavDropdownContent
                                columns={displayData.columns}
                                promo={displayData.promo}
                                footer={getFooter(displayKey)}
                                isAnimating={isAnimating}
                            />
                        )}
                    </div>
                </div>
            </nav>

            <button
                aria-label="toggleSearch"
                onClick={toggleSearch}
                className="flex items-center justify-center p-1.5 lg:p-2.5">
                <Search className="size-5 text-gray-500 hover:text-fuschia dark:text-gray-300 dark:hover:text-fuschia-500" />
            </button>

            <NavDemoButtons />

            {showSearch && <SearchModal isOpen onClose={closeSearch} />}
        </div>
    );
}

export default Navigation;
